import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { ProductCard } from '../components/shop/ProductCard';
import { CtaBand } from '../components/ui/CtaBand';
import { categories } from '../data/categories';
import { products } from '../data/products';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
type SortKey = 'az' | 'price-asc' | 'price-desc' | 'popular';
const sortOptions: {
  value: SortKey;
  label: string;
}[] = [{
  value: 'az',
  label: 'A–Z'
}, {
  value: 'price-asc',
  label: 'Price: low to high'
}, {
  value: 'price-desc',
  label: 'Price: high to low'
}, {
  value: 'popular',
  label: 'Most requested'
}];
export function Shop() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('az');
  const {
    brand
  } = useBrand();
  const activeCategory = params.get('category') ?? 'all';
  useDocumentMeta({
    title: `Shop — ${brand.name}`,
    description: 'Every peptide and medication we offer, with the monthly price, the dose and the pharmacy in the open before you ask for anything.'
  });
  const setCategory = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === 'all') {
      next.delete('category');
    } else {
      next.set('category', slug);
    }
    setParams(next, {
      replace: true
    });
  };
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = products.filter(product => {
      const inCategory = activeCategory === 'all' || product.category === activeCategory;
      const matches = term.length === 0 || product.name.toLowerCase().includes(term) || product.tagline.toLowerCase().includes(term) || product.form.toLowerCase().includes(term);
      return inCategory && matches;
    });
    const sorted = [...filtered];
    if (sort === 'az') sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    if (sort === 'popular') {
      sorted.sort((a, b) => Number(Boolean(b.popular)) - Number(Boolean(a.popular)) || a.name.localeCompare(b.name));
    }
    return sorted;
  }, [activeCategory, query, sort]);
  const grouped = useMemo(() => categories.map(category => ({
    category,
    items: visible.filter(product => product.category === category.slug)
  })).filter(group => group.items.length > 0), [visible]);
  const showGrouped = activeCategory === 'all' && sort === 'az';
  const isFiltered = activeCategory !== 'all' || query.trim().length > 0;
  return <main data-id="element-568" data-mp="1diu04lvv1bgn">
      <PageHeader eyebrow="Shop" title="Everything we treat, and what it costs." intro="Twenty-three treatments with the price, the dose and the form right there on the card. Look around as long as you like — a doctor decides what is right for you before anything gets filled." data-id="element-569" data-mp="1dh0sp4dfan7l" />

      {/* Controls */}
      <section className="sticky top-0 z-30 border-y border-ink/10 bg-cream/95 backdrop-blur" data-id="element-570" data-mp="3g6k48qlccrgv">
        <div className="mx-auto max-w-shell px-6 py-4 md:px-8" data-id="element-571" data-mp="3k6kjgdg9l6oz">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-572" data-mp="brkerdzr1awx">
            <div className="relative w-full lg:max-w-xs" data-id="element-573" data-mp="gpu77gu39xeu">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" strokeWidth={1.75} data-id="element-574" data-mp="gn80fjf0vlxo" />
              <label htmlFor="product-search" className="sr-only" data-id="element-575" data-mp="7r0ew2nsjt73">
                Search treatments
              </label>
              <input id="product-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search treatments" className="w-full rounded-full border border-ink/15 bg-cream-light py-2.5 pl-11 pr-4 text-sm text-ink placeholder:text-ink-muted focus:border-ink" data-id="element-576" data-mp="2sev9z4oylsxa" />
            </div>

            <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0 lg:pb-0" role="tablist" aria-label="Filter by category" data-id="element-577" data-mp="2tyeq8i6pbe2a">
              <FilterChip active={activeCategory === 'all'} onClick={() => setCategory('all')} label="All" count={products.length} data-id="element-578" data-mp="3lpce98y4iahf" />
              {categories.map(category => <FilterChip key={category.slug} active={activeCategory === category.slug} onClick={() => setCategory(category.slug)} label={category.name} count={products.filter(item => item.category === category.slug).length} data-id="element-579" data-mp="2682ip8fdbjc8" />)}
            </div>

            <div className="flex items-center gap-3" data-id="element-580" data-mp="prd7qnvgpjee">
              <label htmlFor="sort" className="whitespace-nowrap text-xs text-ink-muted" data-id="element-581" data-mp="ppshhag6kmmc">
                Sort
              </label>
              <select id="sort" value={sort} onChange={event => setSort(event.target.value as SortKey)} className="rounded-full border border-ink/15 bg-cream-light px-4 py-2.5 text-sm text-ink focus:border-ink" data-id="element-582" data-mp="ujwkxjq8mcio">
                {sortOptions.map(option => <option key={option.value} value={option.value} data-id="element-583" data-mp="1dfwida1zi89n">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Category tiles */}
      {!isFiltered && <section className="bg-cream" data-id="element-584" data-mp="mdatrsqaofgx">
          <div className="mx-auto max-w-shell px-6 pt-14 md:px-8" data-id="element-585" data-mp="mecbgg1tg07m">
            <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-586" data-mp="hfiwskasm35v">
              Browse by what you are working on
            </h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-587" data-mp="vc5wuvy1l9g0">
              {categories.map(category => <li key={category.slug} data-id="element-588" data-mp="15cezosmjht4d">
                  <Link to={`/categories/${category.slug}`} className="group flex h-full items-center justify-between gap-4 rounded-brand border border-ink/10 bg-cream-light p-5 transition-colors duration-150 ease-republic hover:border-ink/40" data-id="element-589" data-mp="15bdis7iueceq">
                    <span data-id="element-590" data-mp="1a3wxj7t9ndl4">
                      <span className="block font-serif text-lg font-semibold text-ink" data-id="element-591" data-mp="1a5r9hwmk7vd2">
                        {category.name}
                      </span>
                      <span className="mt-1 block text-sm text-ink-muted" data-id="element-592" data-mp="1j5ebajj7b36n">{category.tagline}</span>
                    </span>
                    <ArrowRightIcon className="h-5 w-5 shrink-0 text-brick transition-transform duration-200 ease-republic group-hover:translate-x-1" strokeWidth={1.75} data-id="element-593" data-mp="22qoe4to04g5p" />
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Results */}
      <section className="bg-cream" data-id="element-594" data-mp="1jylquzguu1mh">
        <div className="mx-auto max-w-shell px-6 py-14 md:px-8 md:py-16" data-id="element-595" data-mp="1jxkdupga3524">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-596" data-mp="1orol79tghf3q">
            <p className="text-sm text-ink-muted" data-id="element-597" data-mp="1a28xk2p81ps9">
              {visible.length} {visible.length === 1 ? 'treatment' : 'treatments'}
              {activeCategory !== 'all' && ` in ${categories.find(c => c.slug === activeCategory)?.name}`}
            </p>
            {isFiltered && <button type="button" onClick={() => {
            setQuery('');
            setCategory('all');
          }} className="inline-flex items-center gap-1.5 text-sm text-ink transition-colors duration-150 ease-republic hover:text-brick" data-id="element-598" data-mp="15yazlvbmul0s">
                <XIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-599" data-mp="1blixkkbq0i4r" />
                Clear filters
              </button>}
          </div>

          {visible.length === 0 && <div className="rule mt-8 pt-12 text-center" data-id="element-600" data-mp="12kchudgwbbck">
              <p className="font-serif text-2xl font-semibold text-ink" data-id="element-601" data-mp="xrjdvrt9471j">Nothing matches that.</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-ink-soft" data-id="element-602" data-mp="ssf2qvmrnz8k">
                Try another word, or clear the filters. If we do not carry something you were hoping
                for, ask your doctor — they will tell you straight whether it is coming.
              </p>
            </div>}

          {showGrouped ? grouped.map(group => <div key={group.category.slug} className="mt-12 first:mt-8" data-id="element-603" data-mp="2qgivyxjw6b35">
                  <div className="rule flex flex-wrap items-baseline justify-between gap-3 pt-6" data-id="element-604" data-mp="2qimx57qeovzq">
                    <h2 className="font-serif text-2xl font-semibold text-ink" data-id="element-605" data-mp="2vhq3qwm1ba9t">
                      {group.category.name}
                    </h2>
                    <Link to={`/categories/${group.category.slug}`} className="text-sm text-brick transition-colors duration-150 ease-republic hover:text-ink" data-id="element-606" data-mp="34gjxmf7zdmjo">
                      What this covers
                    </Link>
                  </div>
                  <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-607" data-mp="2gpxo0jibvu7k">
                    {group.items.map(product => <ProductCard key={product.slug} product={product} data-id="element-608" data-mp="1o21vlqzskogl" />)}
                  </ul>
                </div>) : visible.length > 0 && <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-609" data-mp="1syscggjf7o32">
                  {visible.map(product => <ProductCard key={product.slug} product={product} data-id="element-610" data-mp="2zx9m7w59a4k1" />)}
                </ul>}
        </div>
      </section>

      <section className="bg-ink text-cream" data-id="element-611" data-mp="34vkwg7zgqh2s">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-612" data-mp="39vfl04vrmuko">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16" data-id="element-613" data-mp="2c9016wfv9kty">
            <div data-id="element-614" data-mp="2c6vts4ire3ox">
              <p className="text-[0.6875rem] uppercase tracking-label text-copper" data-id="element-615" data-mp="2h7jp32e0t6oy">
                How ordering works
              </p>
              <h2 className="mt-4 font-serif text-3xl font-bold leading-tight md:text-4xl" data-id="element-616" data-mp="2m64jgl6lcw5h">
                You will not find an add-to-cart button here.
              </h2>
              <p className="mt-5 max-w-sm text-base leading-relaxed text-cream/70" data-id="element-617" data-mp="1tj0jwvq9pa13">
                Medication should not be one click away from a stranger. Here is what happens
                instead.
              </p>
            </div>
            <ol className="grid gap-8 sm:grid-cols-3" data-id="element-618" data-mp="1qxapy5q73u5k">
              {[{
              step: 'Tell us',
              detail: 'Flag what you are curious about when you fill out your intake.'
            }, {
              step: 'We look',
              detail: 'A doctor reads your labs and writes the plan — or tells you something else would work better.'
            }, {
              step: 'You decide',
              detail: 'You see the full monthly cost and say yes before a dollar moves.'
            }].map((item, index) => <li key={item.step} data-id="element-619" data-mp="137gbxku53zjq" data-mpi={index}>
                  <span className="font-serif text-2xl font-semibold text-copper" data-id="element-620" data-mp="136dmlhcgu55x">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-3 text-base font-medium text-cream" data-id="element-621" data-mp="tbde2r6bmn2l">{item.step}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/70" data-id="element-622" data-mp="o9wbmr86usjw">{item.detail}</p>
                </li>)}
            </ol>
          </div>
          <p className="rule-light mt-12 max-w-4xl pt-6 text-xs leading-relaxed text-cream/60" data-id="element-623" data-mp="9o8sn9dq1u2m">
            Compounded medications and peptides are not FDA-approved products. They are made by
            US-licensed 503A and 503B pharmacies and filled only against a prescription written by a
            doctor licensed in your state.
          </p>
        </div>
      </section>

      <CtaBand data-id="element-624" data-mp="5orm0cmk6qvv" />
    </main>;
}
function FilterChip({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return <button type="button" role="tab" aria-selected={active} onClick={onClick} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors duration-150 ease-republic ${active ? 'bg-ink text-cream' : 'border border-ink/15 text-ink-soft hover:border-ink hover:text-ink'}`} data-id="element-625" data-mp="xd1nf2ilxvvn">
      {label}
      <span className={`ml-2 text-xs ${active ? 'text-cream/60' : 'text-ink-muted'}`} data-id="element-626" data-mp="1h2luo8j1qw1k">{count}</span>
    </button>;
}